import React, { useState, useEffect } from "react";
import clsx from "clsx";
import { useFormChangeHandler } from "hooks/useFormChangeHandler";
import { useSubmitHandler } from "hooks/useSubmitHandler";
import { useCreateData, useUpdateData } from "hooks/useApiHooks";
import Select from 'react-select';
import makeAnimated from 'react-select/animated';
import { getRequest } from "services/api";
import { buildQueryString } from "utils/queryHelpers";

const animatedComponents = makeAnimated();

const RolesSelect = ({ handleChange, defaultValue, isUpdateMode, initialData, setFormData }) => {
    const [rolesOptions, setRolesOptions] = useState([]);
    const [rolesSettings, setRolesSettings] = useState({
        isLoading: false,
        error: false,
        isDisabled: false,
    });

    const fetchRoles = async (search) => {
        setRolesSettings({ ...rolesSettings, isLoading: true, isDisabled: true });
        const queryString = buildQueryString({ search: search || '', all: 1 });
        try {
            const response = await getRequest(`/role${queryString}`);
            if (response?.success) {
                const options = response.data.map((data) => ({ value: data.id, label: data.name }));
                if (isUpdateMode) {
                    const selectedOptions = options.filter((option) => {
                        return initialData.roles.includes(option.label);
                    });
                    setFormData((prev) => ({ ...prev, role_ids: selectedOptions }));
                }
                setRolesOptions(options);
            } else {
                setRolesSettings({ ...rolesSettings, error: true });
            }
        } catch (error) {
            console.error(error);
            setRolesSettings({ ...rolesSettings, error: true });
        } finally {
            setRolesSettings({ ...rolesSettings, isLoading: false, isDisabled: false });
        }
    };

    const selectChange = (selectedOptions) => {
        handleChange({ target: { name: 'role_ids', value: selectedOptions } });
    };

    useEffect(() => {
        fetchRoles();
    }, []);

    return (
        rolesSettings.error ? <div className="text-danger">Failed to fetch roles</div> : <Select
            closeMenuOnSelect={false}
            components={animatedComponents}
            isMulti
            options={rolesOptions}
            isClearable
            onChange={selectChange}
            name="role_ids"
            value={defaultValue}
            isLoading={rolesSettings.isLoading}
            isDisabled={rolesSettings.isDisabled}
            placeholder={rolesSettings.isLoading ? 'Loading Roles...' : 'Select Roles...'}
        />
    );
};

const CreatePage = ({ initialData, onSuccess, onError }) => {
    const API_URL = '/user';

    // State untuk menyimpan form data. Jika initialData tersedia, isi form untuk update
    const [formData, setFormData] = useState({
        // Tambahkan inisialisasi untuk semua field yang diperlukan
        name: '',
        username: '',
        email: '',
        password: null,
        whatsapp: '',
        jobdesc: '',
        work_status: '',
        role_ids: null,
    });

    const isUpdateMode = !!initialData; // Cek apakah ini update mode atau create mode

    // API hook untuk create dan update
    const { createData: createProduct } = useCreateData(API_URL, 'users');
    const { updateData: updateProduct } = useUpdateData(`${API_URL}/${initialData?.id}`, 'users');

    // Gunakan hook untuk menangani perubahan form
    const handleChange = useFormChangeHandler(setFormData);

    // Gunakan hook untuk menangani pengiriman form
    const { handleSubmit, isSubmitting, formError } = useSubmitHandler(
        isUpdateMode ? updateProduct : createProduct,
        onSuccess,
        onError
    );

    const handleBeforeSubmit = (e) => {
        e.preventDefault();

        if (formData.role_ids === null) {
            onError(true, 'Role is required');
            return;
        }

        const payload = {
            ...formData,
            role_ids: formData.role_ids.map((role) => role.value),
        };

        if (formData.password === null) delete payload.password;

        handleSubmit(payload, e);
    };

    useEffect(() => {
        // Jika data produk berubah (untuk update), isi ulang form data
        if (initialData) {
            setFormData(initialData); // Sesuaikan dengan struktur initialData
        }
    }, [initialData]);

    return (
        <form onSubmit={(e) => handleBeforeSubmit(e)} className="d-flex flex-column" style={{ height: '100%' }}>
            <div className="action-sheet-content flex-grow-1">
                <div className="row mb-3 align-items-center">
                    <label htmlFor="name" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Name:</label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.name && 'is-invalid')}
                            id="name"
                            placeholder="Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.name && <div className="invalid-feedback">{formError.name}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="username" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Username:</label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.username && 'is-invalid')}
                            id="username"
                            placeholder="Username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.username && <div className="invalid-feedback">{formError.username}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="email" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Email:</label>
                    <div className="col-sm-8">
                        <input
                            type="email"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.email && 'is-invalid')}
                            id="email"
                            placeholder="Email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.email && <div className="invalid-feedback">{formError.email}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="password" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Password:</label>
                    <div className="col-sm-8">
                        <input
                            type="password"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.password && 'is-invalid')}
                            id="password"
                            placeholder="Password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.password && <div className="invalid-feedback">{formError.password}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="whatsapp" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Whatsapp:</label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.whatsapp && 'is-invalid')}
                            id="whatsapp"
                            placeholder="Whatsapp"
                            name="whatsapp"
                            value={formData.whatsapp}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.whatsapp && <div className="invalid-feedback">{formError.whatsapp}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="jobdesc" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Jobdesc:</label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.jobdesc && 'is-invalid')}
                            id="jobdesc"
                            placeholder="Jobdesc"
                            name="jobdesc"
                            value={formData.jobdesc}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.jobdesc && <div className="invalid-feedback">{formError.jobdesc}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="work_status" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Work Status:</label>
                    <div className="col-sm-8">
                        <input
                            type="text"
                            className={clsx("form-control gotra-form-control rounded-1", formError?.work_status && 'is-invalid')}
                            id="work_status"
                            placeholder="Work Status"
                            name="work_status"
                            value={formData.work_status}
                            onChange={handleChange}
                            disabled={isSubmitting}
                            style={{ fontSize: '14px', height: '35px' }}
                        />
                        {formError?.work_status && <div className="invalid-feedback">{formError.work_status}</div>}
                    </div>
                </div>

                <div className="row mb-3 align-items-center">
                    <label htmlFor="role_ids" className="col-sm-4 col-form-label d-flex justify-content-end" style={{ fontSize: '14px' }}>Role:</label>
                    <div className="col-sm-8">
                        <RolesSelect handleChange={handleChange} defaultValue={formData.role_ids} isUpdateMode={isUpdateMode} initialData={initialData} setFormData={setFormData} />
                        {formError?.role_ids && <div className="invalid-feedback">{formError.role_ids}</div>}
                    </div>
                </div>
            </div>

            <div className="form-group mt-auto d-grid">
                <button type="submit" className="btn btn-primary btn-block" disabled={isSubmitting}>
                    {isUpdateMode ? 'Update Team' : 'Create Team'}
                </button>
            </div>
        </form>
    );
};

export default CreatePage;